import { Tag } from "@blueprintjs/core";
import { ICellRendererParams } from "ag-grid-community";
import { FuelTypeIcon } from "in_queue/icons/fuelType/FuelTypeIcon";
import {
    getStudyCycleLabel,
    isKnownStudyCycleType
} from "in_queue/types/clusterType";
import {
    getConstraintStudyCycle,
    isPriorQueueConstraint,
    ScenarioConstraint
} from "in_queue/types/scenarioResultType";
import { capitalize } from "lodash";
import { formatFuelType } from "types/queueProjectRelatednessType";
import { RegionType } from "types/regionType";
import css from "../TableStyles.module.scss";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FuelTypeCellRenderer: any = (params: ICellRendererParams) => {
    const value = params.value;
    if (!value) return "-";
    return (
        <span className={css["fuel-type-cell"]}>
            <FuelTypeIcon fuelType={value} />
            <span>{capitalize(formatFuelType(value))}</span>
        </span>
    );
};

// Region-aware compact study-cycle label for the Constraints "queue" column: PJM abbreviates
// ("Transition Cycle 2" -> "TC2"); other regions get the "-Cycle" strip (MISO "DPP-2022-Cycle"
// -> "DPP-2022"). Shared by the value getter and the cell renderer so the filter/export value
// and the displayed label can't drift.
export const formatConstraintStudyCycle = (
    region: RegionType | undefined,
    cycle: string | null | undefined
): string => {
    if (!cycle) return "";
    return region === RegionType.PJM && isKnownStudyCycleType(cycle)
        ? getStudyCycleLabel(region, cycle)
        : cycle.replace(/-Cycle$/, "");
};

interface QueueCellProps {
    cycle?: string | null;
    priorQueue?: boolean;
}

// Generic "Study Cycle" cell: renders a (pre-formatted) cycle label plus a "PQ" badge for
// prior-queue rows. Region-agnostic so it can be reused across regions.
export const QueueCellRenderer = ({ cycle, priorQueue }: QueueCellProps) => {
    if (!cycle && !priorQueue) return <>-</>;
    return (
        <span className={css["queue-cell"]}>
            {cycle && <span>{cycle}</span>}
            {priorQueue && (
                <Tag minimal round intent="warning">
                    PQ
                </Tag>
            )}
        </span>
    );
};

// ag-grid adapter binding the generic QueueCellRenderer to a ScenarioConstraint row.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ScenarioConstraintQueueCellRenderer: any = (
    params: ICellRendererParams & { region?: RegionType }
) => {
    const c = params.data as ScenarioConstraint | undefined;
    return (
        <QueueCellRenderer
            cycle={formatConstraintStudyCycle(
                params.region,
                c ? getConstraintStudyCycle(c) : null
            )}
            priorQueue={c ? isPriorQueueConstraint(c) : false}
        />
    );
};
